import { apiVersionMap, type APIKeys } from '../../api/version';
import { ERROR_CODE } from '../../error/code';
import { createError } from '../../error/hybrid-error';
import type { APIAvailableCache, Platforms, ShortPlatforms } from './types';

// Constants
const LOCAL_BUILD_VERSION = 'ZoomBundleVersionNumber';

// Error message templates
const ERROR_MESSAGES = {
  API_NOT_AVAILABLE: (apiName: string) =>
    `${apiName} is not available in current client!`,
  VERSION_NOT_SUPPORTED: (
    platform: Platforms,
    clientVersion: string,
    apiName: string,
    requiredVersion: string
  ) =>
    `The current client ${platform}(${clientVersion}) does not meet the minimum version requirement for the API(${apiName}). Please upgrade the client to: ${requiredVersion}`,
  PLATFORM_NOT_SUPPORTED: (
    platform: Platforms,
    clientVersion: string,
    apiName: string,
    supportedInfo: Record<Platforms, string>
  ) =>
    `The current platform ${platform} (${clientVersion}) does not support API ${apiName}. supported platform: ${JSON.stringify(supportedInfo)}.`,
  NO_COMPATIBILITY_INFO: (apiName: string, validApis: string[]) =>
    `No compatibility information was found for this API(${apiName}). Below are all the valid API: ${validApis.join(',')}`,
  CLIENT_NOT_SUPPORT_CHECK:
    'This client not support isAvailable api, so default allow api call.',
} as const;

const PLATFORM_MAP: Record<Platforms, ShortPlatforms> = {
  mac: 'm',
  windows: 'w',
  linux: 'l',
  ios: 'i',
  android: 'a',
};

const getKeyByValue = (value: ShortPlatforms): Platforms => {
  return Object.keys(PLATFORM_MAP).find(
    (key) => PLATFORM_MAP[key as Platforms] === value
  )! as Platforms;
};

const apiAvailableCache: APIAvailableCache = {};

/**
 * Generate supported platform information for an API
 */
function generateSupportedPlatformInfo(
  apiName: APIKeys
): Record<Platforms, string> {
  return Object.entries(apiVersionMap[apiName] || {}).reduce<
    Record<Platforms, string>
  >(
    (acc, [shortPlatform, version]) => {
      const platform = getKeyByValue(shortPlatform as ShortPlatforms);
      if (platform) {
        acc[platform] = version;
      }
      return acc;
    },
    {} as Record<Platforms, string>
  );
}

/**
 * Check if API is available for current platform and version
 */
function checkApiAvailability(
  apiName: APIKeys,
  platform: Platforms,
  clientVersion: string,
  shortPlatform: ShortPlatforms
): { available: boolean; error?: string } {
  const supportVersion = apiVersionMap[apiName]?.[shortPlatform];

  if (!supportVersion) {
    if (!apiVersionMap[apiName]) {
      return {
        available: false,
        error: ERROR_MESSAGES.NO_COMPATIBILITY_INFO(
          apiName,
          Object.keys(apiVersionMap)
        ),
      };
    }

    const supportedInfo = generateSupportedPlatformInfo(apiName);
    return {
      available: false,
      error: ERROR_MESSAGES.PLATFORM_NOT_SUPPORTED(
        platform,
        clientVersion,
        apiName,
        supportedInfo
      ),
    };
  }

  const isSupported = isVersionSupported(clientVersion, supportVersion);
  if (!isSupported) {
    return {
      available: false,
      error: ERROR_MESSAGES.VERSION_NOT_SUPPORTED(
        platform,
        clientVersion,
        apiName,
        supportVersion
      ),
    };
  }

  return { available: true };
}

/**
 * Calculate availability state for all APIs
 */
function calculateApiAvailabilityStates(
  clientVersion: string,
  shortPlatform: ShortPlatforms
): { [key in APIKeys]?: boolean } {
  const apiAvailableState: { [key in APIKeys]?: boolean } = {};

  for (const key in apiVersionMap) {
    const api = key as APIKeys;
    const supportVersion = apiVersionMap[api][shortPlatform];

    if (supportVersion) {
      apiAvailableState[api] = isVersionSupported(
        clientVersion,
        supportVersion
      );
    } else {
      apiAvailableState[api] = false;
    }
  }

  return apiAvailableState;
}

/**
 * Check if a specific API is available on the current platform and client version
 */
async function isAvailable(
  apiName: APIKeys,
  getSystemParamFn?: () => Promise<any>
): Promise<boolean> {
  // Check cache first
  if (Object.prototype.hasOwnProperty.call(apiAvailableCache, apiName)) {
    if (apiAvailableCache[apiName]) {
      return true;
    } else {
      throw new Error(ERROR_MESSAGES.API_NOT_AVAILABLE(apiName));
    }
  }

  try {
    // Use the provided function or dynamically import to avoid circular dependency
    let getSystemParam = getSystemParamFn;
    if (!getSystemParam) {
      const { getSystemParam: importedGetSystemParam } = await import(
        'jssdk/platform'
      );
      getSystemParam = importedGetSystemParam;
    }

    // Get system parameters
    const systemParam = await getSystemParam();
    const { clientVersion, deviceOs } = systemParam;
    const platform = deviceOs.toLowerCase() as Platforms;
    const shortPlatform = PLATFORM_MAP[platform];

    // Calculate availability for all APIs (for caching purposes)
    const apiAvailableState = calculateApiAvailabilityStates(
      clientVersion,
      shortPlatform
    );

    // Check specific API availability
    if (apiAvailableState[apiName]) {
      apiAvailableCache[apiName] = true;
      return true;
    } else {
      apiAvailableCache[apiName] = false;
      const { error } = checkApiAvailability(
        apiName,
        platform,
        clientVersion,
        shortPlatform
      );
      throw new Error(error);
    }
  } catch (error) {
    // Only catch getSystemParam errors, not the intentionally thrown API availability errors
    if (
      (error instanceof Error &&
        error.message.includes(
          'does not meet the minimum version requirement'
        )) ||
      (error instanceof Error &&
        error.message.includes('does not support API')) ||
      (error instanceof Error &&
        error.message.includes('No compatibility information was found'))
    ) {
      // Re-throw API availability errors
      throw error;
    }

    // If system param check fails, default to allowing API call for better dev experience
    console.warn(ERROR_MESSAGES.CLIENT_NOT_SUPPORT_CHECK, error);
    apiAvailableCache[apiName] = true;
    return true;
  }
}

function isVersionSupported(currentVersion: string, apiVersion: string) {
  // for the better dev experience, skip version check for local build
  if (currentVersion === LOCAL_BUILD_VERSION) {
    console.warn(
      '[hybrid/jssdk] Skip version check for local development build! 🚀'
    );
    return true;
  }
  return notLess(currentVersion, apiVersion);
}

function notLess(currentVersion: string, targetVersion: string) {
  return compareSemVer(currentVersion, targetVersion) >= 0;
}

/**
 * version1 < version2 => -1
 * version1 = version2 =>  0
 * version1 > version2 =>  1
 * @param version1
 * @param version2
 * @return {number}
 */
function compareSemVer(version1: string, version2: string) {
  const splitVersion = (version: string) => version.split('.').map(Number);

  const v1 = splitVersion(version1);
  const v2 = splitVersion(version2);

  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    const num1 = v1[i] || 0;
    const num2 = v2[i] || 0;

    if (num1 < num2) {
      return -1;
    } else if (num1 > num2) {
      return 1;
    }
  }

  return 0;
}

function genError({
  errorCode,
  apiName,
  message,
}: {
  errorCode: number;
  apiName: string;
  message: string;
}): Error {
  return createError({
    code: errorCode,
    api: apiName,
    message,
  });
}

function isInUnifyWebview() {
  return !!(window.unifyConfig || window.android_config);
}

export function genRandomId(prefix = 'id') {
  return prefix + '_' + Date.now() + '_' + Math.random().toString(16).slice(2);
}

/**
 * Clear the API availability cache (for testing purposes)
 * @internal
 */
export function clearApiAvailableCache(): void {
  Object.keys(apiAvailableCache).forEach((key) => {
    delete apiAvailableCache[key];
  });
}

export {
  isAvailable,
  genError,
  isInUnifyWebview,
  ERROR_CODE,
  createError,
  compareSemVer,
};
